/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 *
 *  App Store
 */
import { configure, observable, decorate, reaction, action } from 'mobx';
import SentryApi from 'containers/services/Sentry';
import { firebase } from 'containers/services/Firebase';
configure({ enforceActions: 'observed' });

const sentry = new SentryApi();

class AppStore {
  brand = 'VendorBadge';
  currentPage = '';
  currentPageView = '';
  logLevel = 'debug';

  constructor(stores) {
    this._stores = stores;
    this._env = process.env.REACT_APP_ENVIRONMENT;
    this._project = process.env.REACT_APP_FIREBASE_PROJECT_NAME;
    this._debug = process.env.REACT_APP_DEBUG === 'true';

    if (this._debug) {
      this.debug('App.constructor() Debugging Enabled...');
      this.debug('App.constructor() Serving Portal in ' + this._env + ' environment.');
      this.debug('App.constructor() Env Variables:', process.env);
    }
  }
  get stores() {
    return this._stores;
  }

  get env() {
    return this._env;
  }
  get project() {
    return this._project;
  }
  get page() {
    return this.currentPage;
  }
  get view() {
    return this.currentPageView;
  }
  setPage(value) {
    this.currentPage = value;
  }
  setView(value) {
    this.currentPageView = value;
  }

  /**
   * Legacy Support
   */
  setPageView(value) {
    this.currentPageView = value;
  }

  setDocumentTitle = reaction(
    () => {
      // Detect what version to render
      const brand = this.brand;
      var view = this.currentPageView;
      var page = this.currentPage;
      var env = '';
      var newTitle = '';

      // If Page Name and View Name are the same, only render page name.
      if (this.currentPageView && this.currentPage === this.currentPageView) {
        view = '';
      }

      // Check env
      if (this.env && this.env !== '') {
        env = this.env + ' ';
      }
      // If view exists, add extra formatting.
      view += view === '' ? '' : ' - ';

      newTitle = env + view + page + ' | ' + brand;

      this.debug('App [reaction] Page title updated.', newTitle);
      return newTitle;
    },
    newTitle => (document.title = newTitle)
  );

  /**
   * App debug log handler
   */
  debug(message, data) {
    // If debug enabled, log to console
    const eventDtTm = new Date();
    if (this._debug && this.logLevel === 'debug') {
      console.log(`[debug] [${eventDtTm.toISOString()}] ${message}${data ? ' >>' : ''}`, data ? data : '');
    }
  }

  /**
   * App info log handler
   */
  info(message, data) {
    // If debug enabled, log to console
    const eventDtTm = new Date();
    if (this._debug && this.logLevel === 'info') {
      console.log(`[info] [${eventDtTm.toISOString()}] ${message}${data ? ' >>' : ''}`, data ? data : '');
    }
  }

  /**
   * App error handler
   */
  error(type, error) {
    // If debug enabled, log to console
    const eventDtTm = new Date();
    if (this._debug && (this.logLevel === 'error' || this.logLevel === 'info' || this.logLevel === 'debug')) {
      console.error(`[error] [${eventDtTm.toISOString()}] [${type}] >> `, error ? error : '');
    }

    // Send to Sentry
    sentry.sendError(error);

    // Get enhanced error
    const eError = firebase.getErrorMessage(error);

    if (type === 'user') {
      // Notify User
      this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(eError.message, 'error', 4500);
    }
  }
}
decorate(AppStore, {
  brand: observable,
  currentPage: observable,
  currentPageView: observable,
  logLevel: observable,
  setPage: action,
  setView: action,
  setPageView: action,
  log: action,
  errorHandler: action,
});

export default AppStore;
